// react
import { MouseEvent, useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Grid } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// fields
import { Input } from '../../fields/Input/Input';

// types
import { ErrorCause } from '../../../@types';

// context
import { ContextBanner, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// Props

export interface DialogAddExpenseTypeProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

// Component

export const DialogAddExpenseType = ({ isOpen, onClose, onSuccess }: DialogAddExpenseTypeProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [isLoading, setIsLoading] = useState(false);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { userEntity } = useContext(ContextUserEntity);

	// vars
	const formId = 'form-add-expense-type';

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${formId}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());
		const { name } = entries;

		if (name) {
			try {
				setIsLoading(true);

				const body = {
					...entries,
					entity: userEntity?.id,
					project: project?.id,
				};

				const fetchExpenseType = await fetch(`${process.env.REACT_APP_API_URL}/expense-types`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(body),
				});
				const resAddExpenseType = await fetchExpenseType.json();

				if (resAddExpenseType.error) {
					throw new Error(resAddExpenseType.error.message, {
						cause: {
							id: resAddExpenseType.error.id,
						},
					});
				}

				onSuccess();

				setBanner({
					hasClose: true,
					message: t('alert.expenseTypeCreated', { name }),
					severity: 'success',
				});
			} catch (error) {
				const err = error as Error;
				const cause = err.cause as ErrorCause;

				let message = t('error.default');

				if (cause.id === 'EXPENSE_TYPE_NAME_NON_EXISTENT') {
					message = t('error.expenseTypeNameNonExistent');
				}

				if (cause.id === 'EXPENSE_TYPE_UNAUTHORIZED_POST') {
					message = t('error.unauthorized');
				}

				setBanner({
					hasClose: true,
					message,
					severity: 'error',
				});
			} finally {
				setIsLoading(false);
			}
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: onClose,
				},
				{
					children: t('addExpenseType'),
					loading: isLoading,
					form: formId,
					type: 'submit',
					variant: 'contained',
				},
			]}
			hasClose={true}
			isOpen={isOpen}
			maxWidth={700}
			onClose={onClose}
			title={{
				children: t('addExpenseType'),
				variant: 'h3',
			}}>
			<form id={formId} name="formAddExpenseType" onSubmit={handleSubmit}>
				<Grid container spacing={2} item xs={12}>
					<Grid item xs={12}>
						<Input
							aria-label={t('addExpenseType')}
							disabled={isLoading}
							fullWidth
							id={`${formId}-name`}
							name={'name'}
							required={true}
						/>
					</Grid>
				</Grid>
			</form>
		</DialogActions>
	);
};
